import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import { updateEmailAddress } from "../../../api/mutation";
import TextInput from "../../../components/InputFields/TextInput";
import { V5, V10, V12 } from "../../../components/InputFields/validator/validators";
import { ModelState } from "../../../interfaces/modelState";
import { force } from "../../../typeguards/force";
import { MutationLayout } from "../MutationLayout";
import { emailAddressMutationSitecoreLabels } from "./EmailAddressMutation.labels";
import { emailAddressMutationReducer, initialState } from "./EmailAddressMutation.state";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
  policyNr: string;
}

export const EmailAddressMutation = ({ fields, policyNr }: Props) => {
  const history = useHistory();

  const [{ data, loading, modelState }, dispatch] = useReducer(emailAddressMutationReducer, initialState);

  // create a copy of the object, because the fields try to mutate it
  const copyOfModelState = { ...modelState };

  const handleUpdateOldEmailAddress = (oldEmailAddress: string) => {
    dispatch({ type: "UPDATE_EMAIL_ADDRESS_MUTATION_DATA", payload: { oldEmailAddress } });
  };

  const handleUpdateNewEmailAddress = (newEmailAddress: string) => {
    dispatch({ type: "UPDATE_EMAIL_ADDRESS_MUTATION_DATA", payload: { newEmailAddress } });
  };

  const setModelState = (newModelState: ModelState) => {
    dispatch({ type: "UPDATE_EMAIL_ADDRESS_MUTATION_ERROR", payload: newModelState });
  };

  const handleEmailAddressMutationClick = async () => {
    try {
      dispatch({ type: "UPDATE_EMAIL_ADDRESS_MUTATION_FETCH_START" });
      const request = {
        newEmailAddress: force(data.newEmailAddress),
        oldEmailAddress: data.oldEmailAddress,
      };
      const response = await updateEmailAddress({ ...request, policyNumber: policyNr });

      if (response.modelState) {
        dispatch({
          type: "UPDATE_EMAIL_ADDRESS_MUTATION_FETCH_ERROR",
          payload: response.modelState,
        });
      } else {
        // remove the query parameters
        window.history.replaceState(null, "", window.location.pathname);
        history.push(fields.ThankYouPage.url);
      }
    } catch {
      dispatch({
        type: "UPDATE_EMAIL_ADDRESS_MUTATION_FETCH_ERROR",
        payload: { global: ["Er is een onverwachte fout opgetreden. Probeer het opnieuw."] },
      });
    }
  };

  const canSubmitForm = Boolean(data.newEmailAddress);

  return (
    <MutationLayout
      canSubmitForm={canSubmitForm}
      handleSubmit={handleEmailAddressMutationClick}
      loading={loading}
      modelState={copyOfModelState}
      title="E-mailadres wijzigen"
      CardContent={
        <>
          <TextInput
            fieldData={emailAddressMutationSitecoreLabels}
            name="OldEmailAddress"
            validators={[V10(99), V12]}
            setModelState={setModelState}
            inputValue={data.oldEmailAddress}
            modelState={copyOfModelState}
            fieldName="OldEmailAddress"
            propertyName="model.OldEmailAddress"
            onBlur={handleUpdateOldEmailAddress}
          />
          <TextInput
            fieldData={emailAddressMutationSitecoreLabels}
            name="NewEmailAddress"
            validators={[V5, V10(99), V12]}
            setModelState={setModelState}
            inputValue={data.newEmailAddress}
            modelState={copyOfModelState}
            fieldName="NewEmailAddress"
            propertyName="model.NewEmailAddress"
            onBlur={handleUpdateNewEmailAddress}
            required
          />
        </>
      }
      InfoContent={
        <>
          <h6>Verwerkingstermijn</h6>
          <p>
            Wijzigingen zijn niet direct zichtbaar in het DELAportaal De verwerkingstermijn bedraagt maximaal 2
            werkdagen. Er wordt <strong>geen</strong> nieuwe polis afgegeven.
          </p>

          <h6>Nieuw e-mailadres</h6>
          <p>
            Was er nog geen e-mailadres bij ons bekend? Dan vul je het veld ‘Huidig e-mailadres’ niet in. Je geeft dan
            enkel het nieuwe e-mailadres op in het veld onder ‘Nieuw e-mailadres’.
          </p>
        </>
      }
    />
  );
};
